import { Box, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, Row, useReactTable } from '@tanstack/react-table';
import { groupBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { BiMenu } from 'react-icons/bi';
import { RiBook2Line, RiExchangeLine, RiProhibitedLine, RiSearchEyeLine, RiStackLine } from 'react-icons/ri';

import { GroupingColumn } from '@/cip/Optimizer/OptimizationResults/TradeExecution/TradeExecution';
import { CopiableGuid } from '@/components/CopiableGuid';

import { type EnrichedOptimizerTrade } from '../../../_openapi/portfolio-optimizer';
import { AssetClassTag } from '../../../widgets/AssetClassTag';
import {
    baseCol,
    excludeTradesCol,
    expanderCol,
    moneyCol,
    numberCol,
    percentageCol,
    textCol,
} from '../../../widgets/Table/columnHelpers/colDef';
import { Table } from '../../../widgets/Table/Table';
import { TradeSideTag } from '../../../widgets/TradeSideTag';
import { getTradeAggregates, getTradeSide, getTradeValueAsPercentOfNav } from './tradeExecutionUtils';

export const TradeEntry = <T extends { trades: EnrichedOptimizerTrade[] }>({
    row,
    secondaryGrouping,
    viewPortfolio,
    viewAsset,
    excludeTrades,
    viewTrade,
    viewAdvanced,
}: {
    row: Row<T>;
    secondaryGrouping?: GroupingColumn;
    viewPortfolio: (pId: string) => void;
    viewAsset: (sId: string) => void;
    viewTrade: (trade: EnrichedOptimizerTrade) => void;
    excludeTrades: (trades: EnrichedOptimizerTrade[]) => void;
    viewAdvanced: (portfolioGuid: string, pId: string) => void;
}) => {
    if (!secondaryGrouping) {
        return (
            <TradesTable
                trades={row.original.trades}
                viewPortfolio={viewPortfolio}
                viewAsset={viewAsset}
                excludeTrades={excludeTrades}
                viewTrade={viewTrade}
                viewAdvanced={viewAdvanced}
            />
        );
    }

    return (
        <GroupedTable
            row={row}
            secondaryGrouping={secondaryGrouping}
            viewPortfolio={viewPortfolio}
            viewAsset={viewAsset}
            excludeTrades={excludeTrades}
            viewTrade={viewTrade}
            viewAdvanced={viewAdvanced}
        />
    );
};

const columnHelper = createColumnHelper<any>();
const GroupedTable = <T extends { trades: EnrichedOptimizerTrade[] }>({
    row,
    secondaryGrouping,
    viewPortfolio,
    viewAsset,
    excludeTrades,
    viewTrade,
    viewAdvanced,
}: {
    row: Row<T>;
    secondaryGrouping: GroupingColumn;
    viewPortfolio: (pId: string) => void;
    viewAsset: (sId: string) => void;
    viewTrade: (trade: EnrichedOptimizerTrade) => void;
    excludeTrades: (trades: EnrichedOptimizerTrade[]) => void;
    viewAdvanced: (portfolioGuid: string, pId: string) => void;
}) => {
    const data = useMemo(() => {
        const grouped: { [k: string]: EnrichedOptimizerTrade[] } = groupBy(
            row.original.trades,
            getSecondaryGroupingKeySelector(secondaryGrouping),
        );

        return Object.entries(grouped).map(([grouping, trades]) => {
            return {
                grouping,
                trades,
                ...getTradeAggregates(trades),
            };
        });
    }, [row.original.trades, secondaryGrouping]);

    const columns = [
        columnHelper.display(expanderCol()),
        columnHelper.accessor('grouping', textCol({ id: secondaryGrouping })),
        columnHelper.accessor('tradesCount', numberCol({ id: 'tradesCount', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalQuantity', numberCol({ id: 'totalQuantity', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalValue', moneyCol({ id: 'totalValue' })),
        columnHelper.display(excludeTradesCol(excludeTrades)),
    ];

    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
        },
        data,
        columns,
        getSubRows: (originalRow) => originalRow.trades,
        getRowCanExpand: ({ original }) => original.trades.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        sortDescFirst: false,
    });

    const render = useCallback(
        (props: { row: Row<T> }) => (
            <TradesTable
                trades={props.row.original.trades}
                excludeTrades={excludeTrades}
                viewAsset={viewAsset}
                viewPortfolio={viewPortfolio}
                viewTrade={viewTrade}
                viewAdvanced={viewAdvanced}
            />
        ),
        [excludeTrades, viewAsset, viewPortfolio, viewTrade, viewAdvanced],
    );

    return <Table table={table} SubComponent={render} allowXOverflow />;
};

const tradeEntryColumnHelper = createColumnHelper<EnrichedOptimizerTrade>();
const TradesTable = ({
    trades,
    viewPortfolio,
    viewAsset,
    excludeTrades,
    viewTrade,
    viewAdvanced,
}: {
    trades: EnrichedOptimizerTrade[];
    viewPortfolio: (pId: string) => void;
    viewAsset: (sId: string) => void;
    viewTrade: (trade: EnrichedOptimizerTrade) => void;
    excludeTrades: (trades: EnrichedOptimizerTrade[]) => void;
    viewAdvanced: (portfolioGuid: string, pId: string) => void;
}) => {
    const columns = useMemo(() => {
        return [
            tradeEntryColumnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => (
                    <Menu isLazy>
                        <MenuButton
                            size="xs"
                            as={IconButton}
                            aria-label="Options"
                            icon={<Icon boxSize={3} as={BiMenu} />}
                            w="20px"
                            minW="20px"
                            h="20px"
                            variant="outline"
                            borderRadius="sm"
                        />
                        <Portal>
                            <MenuList>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiBook2Line} />}
                                    onClick={() => viewPortfolio(row.original.pId)}
                                >
                                    Portfolio details
                                </MenuItem>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiStackLine} />}
                                    onClick={() => viewAsset(row.original.sId)}
                                >
                                    Security details
                                </MenuItem>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiExchangeLine} />}
                                    onClick={() => viewTrade(row.original)}
                                >
                                    Trade details
                                </MenuItem>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiSearchEyeLine} />}
                                    disabled={!row.original.portfolioGuid}
                                    onClick={() =>
                                        row.original.portfolioGuid &&
                                        viewAdvanced(row.original.portfolioGuid, row.original.pId)
                                    }
                                >
                                    Advanced view
                                </MenuItem>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiProhibitedLine} />}
                                    onClick={() => excludeTrades([row.original])}
                                >
                                    Exclude Trade
                                </MenuItem>
                            </MenuList>
                        </Portal>
                    </Menu>
                ),
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                },
            }),
            tradeEntryColumnHelper.accessor('portfolioGuid', {
                ...baseCol({ id: 'portfolioGuid' }),
                cell: ({ getValue }) => <CopiableGuid className="text-white" guid={getValue()} showBorder={false} />,
            }),
            tradeEntryColumnHelper.accessor('aC', textCol({ id: 'aC' })),
            tradeEntryColumnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
            tradeEntryColumnHelper.accessor(getTradeSide, {
                ...baseCol({ id: 'side' }),
                cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
            }),
            tradeEntryColumnHelper.accessor('asset.assetClass', {
                ...baseCol({ id: 'assetClass' }),
                cell: ({ getValue }) => <AssetClassTag assetClass={getValue()} />,
            }),
            tradeEntryColumnHelper.accessor('asset.assetType', textCol({ id: 'assetType' })),
            tradeEntryColumnHelper.accessor('asset.symbol', textCol({ id: 'symbol' })),
            tradeEntryColumnHelper.accessor('asset.description', textCol({ id: 'description' })),
            tradeEntryColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
            tradeEntryColumnHelper.accessor('orderPrice', moneyCol({ id: 'orderPrice' })),
            tradeEntryColumnHelper.accessor('tradeValue', moneyCol({ id: 'tradeValue' })),
            tradeEntryColumnHelper.accessor(getTradeValueAsPercentOfNav, percentageCol({ id: 'percentOfNav' })),
        ];
    }, [excludeTrades, viewAsset, viewPortfolio, viewTrade, viewAdvanced]);

    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
        },
        data: trades,
        initialState: {
            expanded: true,
        },
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        sortDescFirst: false,
    });

    return <Table table={table} allowXOverflow />;
};

const getSecondaryGroupingKeySelector = (
    secondaryGrouping: GroupingColumn,
): ((trade: EnrichedOptimizerTrade) => string | undefined) => {
    switch (secondaryGrouping) {
        case 'portfolioGuid':
            return tradePortfolioGuidSelector;
        case 'side':
            return getTradeSide;
        case 'allocationType':
            return tradeAllocationTypeSelector;
        case 'symbol':
            return tradeSymbolSelector;
        case 'assetClass':
            return tradeAssetClassSelector;
    }
};

const tradePortfolioGuidSelector = (trade: EnrichedOptimizerTrade) => trade.portfolioGuid;
const tradeAllocationTypeSelector = (trade: EnrichedOptimizerTrade) => trade.allocationType;
const tradeSymbolSelector = (trade: EnrichedOptimizerTrade) => trade.asset?.symbol;
const tradeAssetClassSelector = (trade: EnrichedOptimizerTrade) => trade.asset?.assetClass;
